import { toCompatImageUrl } from './imageCdn';
/**
 * Utility functions for generating Google-readable SEO metadata and JSON-LD Product structured data.
 */

/**
 * Capitalize & format brand name.
 */
const formatBrandName = brand => {
  if (!brand) return '';
  let brandStr = brand;
  if (typeof brand === 'object') {
    brandStr = brand.label || brand.name || brand.option || '';
  }
  let formatted = String(brandStr).replace(/([a-z0-9])([A-Z])/g, '$1 $2').trim();
  return formatted
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Format string or object field value into clean text.
 */
const formatValue = val => {
  if (!val) return '';
  if (typeof val === 'object') {
    return val.label || val.name || val.option || '';
  }
  return String(val).trim();
};

/**
 * Extracts structured listing attributes from publicData and attributes.
 *
 * @param {Object} listing entity
 * @param {Object} config app configuration
 * @returns {Object} normalized structured product data
 */
export const getListingStructuredData = (listing, config = {}) => {
  const attributes = listing?.attributes || {};
  const publicData = attributes.publicData || {};
  const title = (attributes.title || '').trim();
  const description = (attributes.description || '').trim();

  // Brand
  const brandRaw = publicData.brand || publicData.brandName || '';
  const brand = formatBrandName(brandRaw);

  // Colour / Color
  const colorRaw = publicData.color || publicData.colour || publicData.itemColor || '';
  const color = formatValue(colorRaw);

  // Size
  const sizeRaw =
    publicData.size ||
    publicData.clothingSize ||
    publicData.shoeSize ||
    publicData.sizeOption ||
    publicData.itemSize ||
    '';
  const size = formatValue(sizeRaw);

  // Category & Subcategory
  const categoryLevel1 = formatValue(publicData.categoryLevel1 || publicData.category);
  const categoryLevel2 = formatValue(publicData.categoryLevel2);
  const categoryLevel3 = formatValue(publicData.categoryLevel3);
  const subcategory = formatValue(
    categoryLevel3 || categoryLevel2 || publicData.subcategory || categoryLevel1
  );
  const category = categoryLevel1 || subcategory;

  // Condition
  const conditionRaw = formatValue(publicData.condition || publicData.itemCondition || '');

  // Price & Currency
  const priceObj = attributes.price;
  let priceNumber = null;
  if (priceObj && typeof priceObj.amount === 'number') {
    priceNumber = (priceObj.amount / 100).toFixed(2);
  }
  const priceCurrency = 'ZAR';

  // Availability mapping:
  // Active listing (state !== 'closed' & quantity > 0) -> InStock
  // Sold/unavailable -> OutOfStock
  const isClosed = attributes.state === 'closed';
  const currentStock = listing?.currentStock?.attributes?.quantity;
  const isAvailable = !isClosed && (currentStock === undefined || currentStock > 0);
  const availability = isAvailable
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  // Condition mapping:
  // New -> NewCondition
  // Like New / Used -> UsedCondition
  let itemCondition = 'https://schema.org/UsedCondition';
  if (conditionRaw) {
    const condLower = conditionRaw.toLowerCase();
    if (condLower === 'new' || (condLower.includes('new') && !condLower.includes('like'))) {
      itemCondition = 'https://schema.org/NewCondition';
    } else {
      itemCondition = 'https://schema.org/UsedCondition';
    }
  }

  // SKU / Listing ID
  const sku = listing?.id?.uuid || listing?.id || '';

  return {
    title,
    description,
    brand,
    color,
    size,
    category,
    subcategory,
    conditionRaw,
    itemCondition,
    price: priceNumber,
    priceCurrency,
    availability,
    sku,
  };
};

/**
 * Checks if a product title is missing or generic.
 */
const isGenericTitle = title => {
  if (!title) return true;
  const t = title.toLowerCase().trim();
  const genericTerms = [
    'item',
    'product',
    'listing',
    'clothing',
    'shirt',
    'dress',
    'pants',
    'jeans',
    'shoes',
    'untitled',
    'new listing',
    'top',
  ];
  return genericTerms.includes(t) || t.length < 4;
};

/**
 * Generates the HTML <title> tag text for a product page.
 *
 * SEO title fallback rule:
 * If the seller title is too generic or missing:
 *   Brand + Colour + Subcategory + Size | Kloset Klub
 *   Example: Zara Blue Wide Leg Jeans Size 12 | Kloset Klub
 * Else:
 *   Seller Title | Kloset Klub
 *
 * @param {Object} listing
 * @param {Object} config
 * @returns {string} SEO Title
 */
export const generateSeoTitle = (listing, config = {}) => {
  const marketplaceName = config.marketplaceName || 'Kloset Klub';
  const structured = getListingStructuredData(listing, config);

  const { title, brand, color, subcategory, size } = structured;
  const hasStructuredFields = brand || color || subcategory || size;

  if (isGenericTitle(title) && hasStructuredFields) {
    const formattedSize = size
      ? size.toLowerCase().includes('size')
        ? size
        : `Size ${size}`
      : '';
    const fallbackParts = [brand, color, subcategory, formattedSize].filter(Boolean);
    if (fallbackParts.length > 0) {
      return `${fallbackParts.join(' ')} | ${marketplaceName}`;
    }
  }

  if (title) {
    if (title.toLowerCase().includes(marketplaceName.toLowerCase())) {
      return title;
    }
    return `${title} | ${marketplaceName}`;
  }

  const formattedSize = size
    ? size.toLowerCase().includes('size')
      ? size
      : `Size ${size}`
    : '';
  const fallbackParts = [brand, color, subcategory, formattedSize].filter(Boolean);
  if (fallbackParts.length > 0) {
    return `${fallbackParts.join(' ')} | ${marketplaceName}`;
  }

  return `Product | ${marketplaceName}`;
};

/**
 * Generates JSON-LD Product structured data object.
 *
 * Schema fields:
 * - name
 * - description
 * - image
 * - sku
 * - brand ({ @type: 'Brand', name: brand })
 * - color
 * - size
 * - category
 * - url
 * - offers: { price, priceCurrency: 'ZAR', availability, itemCondition, url }
 *
 * @param {Object} listing
 * @param {Object} config
 * @param {Object} location
 * @returns {Object} JSON-LD Schema
 */
export const generateProductSchema = (listing, config = {}, location = {}) => {
  const structured = getListingStructuredData(listing, config);
  const marketplaceRootURL = config.marketplaceRootURL || '';

  const pathname = location?.pathname || '';
  const search = location?.search || '';
  const hash = location?.hash || '';
  const productURL = marketplaceRootURL
    ? `${marketplaceRootURL}${pathname}${search}${hash}`
    : typeof window !== 'undefined'
    ? window.location.href
    : '';

  const {
    title,
    description,
    brand,
    color,
    size,
    category,
    subcategory,
    itemCondition,
    price,
    priceCurrency,
    availability,
    sku,
  } = structured;

  // Extract images
  const images = (listing?.images || [])
    .map(image => {
      const variants = image?.attributes?.variants || {};
      return (
        variants['facebook']?.url ||
        variants['landscape-crop']?.url ||
        variants['default']?.url ||
        image?.attributes?.sizes?.[0]?.url ||
        null
      );
    })
    .filter(Boolean)
    // Google reads these: never-AVIF/WebP copy from our image cache (when enabled)
    .map(toCompatImageUrl);

  const seoTitle = generateSeoTitle(listing, config);

  const schema = {
    '@context': 'http://schema.org',
    '@type': 'Product',
    name: seoTitle || title,
    description: description || title,
    url: productURL,
  };

  if (images.length > 0) {
    schema.image = images;
  }

  if (sku) {
    schema.sku = sku;
  }

  if (brand) {
    schema.brand = {
      '@type': 'Brand',
      name: brand,
    };
  }

  if (color) {
    schema.color = color;
  }

  if (size) {
    schema.size = size;
  }

  if (subcategory || category) {
    schema.category = subcategory || category;
  }

  schema.offers = {
    '@type': 'Offer',
    url: productURL,
    priceCurrency: priceCurrency || 'ZAR',
    availability,
    itemCondition,
  };

  if (price !== null) {
    schema.offers.price = price;
  }

  return schema;
};
